import { useEffect, useRef, useState } from "react";
import { Alert, Button, Stack, TextField, Typography } from "@mui/material";
import { isApiError } from "@bw/shared/api";
import { useAuth } from "@bw/shared/hooks";
import { useLocation, useNavigate } from "react-router-dom";
import { useRequestLesson } from "../hooks/useRequestLesson";
import { GuardianAwareError } from "../../../common/guardian/GuardianAwareError";

const TOPIC = "Coaching lessons";
const DRAFT_KEY = "coach-contact-draft";
const DRAFT_TTL_MS = 30 * 60 * 1000;
const PROMPTS = ["I'm new to badminton and looking for lessons.", "I'd like to work on my technique.", "Do you have availability for private coaching?"];

type GuestDraft = { coachProfileId: string; facilityId: string; message: string; savedAt: number };

function readDraft(coachProfileId: string, facilityId: string): string {
  try {
    const raw = sessionStorage.getItem(DRAFT_KEY);
    if (!raw) return "";
    const draft = JSON.parse(raw) as GuestDraft;
    if (draft.coachProfileId !== coachProfileId || draft.facilityId !== facilityId ||
      typeof draft.message !== "string" || typeof draft.savedAt !== "number" ||
      Date.now() - draft.savedAt > DRAFT_TTL_MS || draft.savedAt > Date.now()) return "";
    return draft.message.slice(0, 2000);
  } catch {
    return "";
  }
}

/** One message opens the canonical coach inquiry; scheduling and quotes happen in that thread. */
export function RequestLessonForm({ coachProfileId, facilityId }: { coachProfileId: string; facilityId: string }) {
  const { user } = useAuth();
  return <CoachContactFields key={`${user?.uid ?? "guest"}:${coachProfileId}:${facilityId}`} coachProfileId={coachProfileId} facilityId={facilityId} />;
}

function CoachContactFields({ coachProfileId, facilityId }: { coachProfileId: string; facilityId: string }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const request = useRequestLesson();
  const [message, setMessage] = useState(() => readDraft(coachProfileId, facilityId));
  const [storageError, setStorageError] = useState(false);
  const inFlight = useRef(false);
  const command = useRef<{ fingerprint: string; key: string } | null>(null);
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(DRAFT_KEY);
      if (!raw) return;
      const draft = JSON.parse(raw) as GuestDraft;
      const invalid = typeof draft.message !== "string" || typeof draft.savedAt !== "number" ||
        Date.now() - draft.savedAt > DRAFT_TTL_MS || draft.savedAt > Date.now();
      const matching = draft.coachProfileId === coachProfileId && draft.facilityId === facilityId;
      if (invalid || (user && matching)) sessionStorage.removeItem(DRAFT_KEY);
    } catch {
      try { sessionStorage.removeItem(DRAFT_KEY); } catch { /* Storage is unavailable. */ }
    }
  }, [coachProfileId, facilityId, user]);

  function handleSubmit(event: React.FormEvent) {
    event.preventDefault();
    const content = message.trim();
    if (!content || inFlight.current || request.isPending) return;
    if (!user) {
      try {
        sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ coachProfileId, facilityId, message: content, savedAt: Date.now() } satisfies GuestDraft));
      } catch {
        setStorageError(true);
        return;
      }
      const returnParams = new URLSearchParams(location.search);
      returnParams.set("facility_id", facilityId);
      returnParams.set("message", "1");
      navigate(`/login?returnUrl=${encodeURIComponent(`${location.pathname}?${returnParams.toString()}`)}`);
      return;
    }
    inFlight.current = true;
    const body = { target: { kind: "coach" as const, coach_profile_id: coachProfileId, facility_id: facilityId }, topic: TOPIC, message: content };
    const fingerprint = JSON.stringify([user.uid, body]);
    if (command.current?.fingerprint !== fingerprint) command.current = { fingerprint, key: crypto.randomUUID() };
    request.mutate({ ...body, idempotency_key: command.current.key }, {
      onSuccess: (result) => navigate(`/community/inquiries/${encodeURIComponent(result.conversation_id)}`),
      onError: (error) => {
        if (isApiError(error) && error.code === "INQUIRY_ALREADY_OPEN" && typeof error.details?.conversation_id === "string") {
          const conversationId = error.details.conversation_id;
          navigate(`/community/inquiries/${encodeURIComponent(conversationId)}`, {
            state: { coachingInquiryDraft: { actorId: user.uid, conversationId, content } },
          });
        }
      },
      onSettled: () => { inFlight.current = false; },
    });
  }

  const error = request.error;
  const conflict = isApiError(error) && error.code === "INQUIRY_ALREADY_OPEN" && typeof error.details?.conversation_id === "string";
  return <Stack component="form" spacing={2} onSubmit={handleSubmit} sx={{ maxWidth: 600 }}>
    <Typography variant="body2" color="text.secondary">Say hello and tell the coach what you want to work on. You can discuss timing and pricing in the conversation.</Typography>
    {!message.trim() ? <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
      {PROMPTS.map((prompt, index) => <Button key={prompt} type="button" variant="outlined" size="small" disabled={request.isPending} onClick={() => setMessage(prompt)}>{["I'm a beginner", "Improve my game", "Ask about availability"][index]}</Button>)}
    </Stack> : null}
    <TextField label="Your message" multiline minRows={3} required fullWidth value={message} onChange={(event) => setMessage(event.target.value)} disabled={request.isPending} slotProps={{ htmlInput: { maxLength: 2000 } }} placeholder="Hi! I'm looking for help with…" />
    {storageError ? <Alert severity="error">We couldn&apos;t save your draft for sign-in. Please allow session storage and try again.</Alert> : null}
    {error && !conflict ? (
      <GuardianAwareError error={error}>
        <Alert severity="error">{isApiError(error) ? error.message : "Could not send your message. Please try again."}</Alert>
      </GuardianAwareError>
    ) : null}
    <Button type="submit" variant="contained" disabled={request.isPending || !message.trim()} sx={{ alignSelf: "flex-start" }}>
      {request.isPending ? "Opening conversation…" : user ? "Send message" : "Sign in to send"}
    </Button>
  </Stack>;
}
